import { Dispatch, FC, ReactElement, SetStateAction, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { HTTP_STATUS } from 'models';

import {
  ExtendedInventeringPreliminar,
  usePatchPubliceraAktivitetPreliminarMutation
} from 'api/endpoints/aktivitetPreliminarSlice';
import { formatDateWithTime } from 'utilities/dateUtilities';

const PubliceraInventeringDialog: FC<{
  id: string;
  setPatchIsLoading: Dispatch<SetStateAction<boolean>>;
}> = ({ id, setPatchIsLoading }): ReactElement => {
  const dispatch = useAppDispatch();
  const { unsetDialog, setDialog } = useDialog();
  const navigate = useNavigate();

  const [patchPubliceraAktivitetPreliminar, patchPubliceraInventeringResults] =
    usePatchPubliceraAktivitetPreliminarMutation();
  const {
    data: patchData,
    isSuccess: patchIsSuccess,
    isLoading: patchIsLoading,
    isError: patchIsError,
    error: patchError,
    isUninitialized: patchIsUninitialized
  } = patchPubliceraInventeringResults;

  useEffect(() => {
    setPatchIsLoading(patchIsLoading);
  }, [patchIsLoading, setPatchIsLoading]);

  useEffect(() => {
    if (patchIsError) {
      setDialog(RaaDialog, {
        header: 'Kan inte publiceras',
        close: unsetDialog,
        proceed: unsetDialog,
        ariaDescribedBy: 'dialog-description',
        children: (
          <div className="tw-mt-8">
            <p id="dialog-description">{'message' in patchError ? patchError.message : 'Något gick snett'}</p>
          </div>
        )
      });
    }
  }, [dispatch, patchError, patchIsError, setDialog, unsetDialog]);

  useEffect(() => {
    if (patchIsSuccess) {
      unsetDialog();

      // 201 PUBLICERAD utan granskning
      if (patchData.http_status_code === HTTP_STATUS.CREATED) {
        setDialog(RaaDialog, {
          header: 'Publiceringen godkändes',
          close: unsetDialog,
          proceed: () => {
            unsetDialog();
            navigate(`/aktivitet/${id}`);
          },
          ariaDescribedBy: 'dialog-description',
          children: (
            <p id="dialog-description" className="tw-mt-8">
              Ingen manuell granskning krävdes.
            </p>
          )
        });
        // 202 Skickad till Granska
      } else if (patchData.http_status_code === HTTP_STATUS.ACCEPTED) {
        const inventeringPreliminar = patchData as ExtendedInventeringPreliminar;

        setDialog(RaaDialog, {
          header: 'Mottagen för granskning',
          close: unsetDialog,
          proceed: unsetDialog,
          ariaDescribedBy: 'dialog-description',
          children: (
            <div className="tw-mt-8">
              <p id="dialog-description">
                Vi har tagit emot informationen och kommer att publicera den efter godkänd granskning av personuppgifter
                och sekretess.
              </p>
              {inventeringPreliminar && (
                <p>
                  Ärendenummer: {inventeringPreliminar.aktivitet_registrering_status.arendenummer}, mottaget{' '}
                  {formatDateWithTime(inventeringPreliminar.aktivitet_registrering_status.status_datum_inskickad)}
                </p>
              )}
            </div>
          )
        });
      }
    }
  }, [id, navigate, patchIsSuccess, patchData, setDialog, unsetDialog]);

  return (
    <RaaDialog
      header="Skicka till publicering?"
      proceed={() => patchPubliceraAktivitetPreliminar(id)}
      cancel={unsetDialog}
      close={patchIsUninitialized && !patchIsLoading ? unsetDialog : undefined}
      showLoadingSpinner={patchIsLoading}
      proceedButtonText="publicera"
      cancelButtonText="avbryt"
      ariaDescribedBy="dialog-description"
    >
      <p id="dialog-description" className="tw-my-4">
        Inskickad information blir offentlig handling och kommer att publiceras efter Riksantikvarieämbetets granskning.
      </p>
    </RaaDialog>
  );
};

export default PubliceraInventeringDialog;
